import { FC, ReactNode, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ContentSwitcher,
  InlineLoading,
  Switch,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import {
  Add,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Connect,
  Information,
  Renew,
  Reset,
  Subtract,
  Unlink,
} from '@carbon/react/icons';
import {
  type GunBarrelSelectionInfo,
  type GunBarrelEditAxis,
} from 'components/charts/gun-barrel-chart';
import type { TrimFrom } from './ltd-lateral-trim';
import { formatNumber } from 'utils/number';
import { UnitBuilderNotesSection } from './unit-builder-notes-section';
import style from './unit-builder-modal.module.scss';

/** Nudge steps, as the mock states: 100 ft across, 10 ft deep. */
const X_STEP_FT = 100;
const TVD_STEP_FT = 10;
const LENGTH_STEP_FT = 100;

interface UnitBuilderEditRailProps {
  /** Formation of the scoped type curve. */
  formationCode: string | null;
  /** Step the scoped formation — up is shallower, down is deeper. */
  onStepFormation?: (delta: 1 | -1) => void;
  canStepUp?: boolean;
  canStepDown?: boolean;

  showSlotLabels: boolean;
  onShowSlotLabelsChange: (value: boolean) => void;

  /** Re-lay the whole unit. */
  onGenerateInventory?: () => void;
  generating?: boolean;

  /** Re-cut only the slots that were moved. */
  onUpdateGeometries?: () => void;
  updating?: boolean;
  /** How many slots carry any pending geometry edit — a position move OR a
   *  lateral-length trim (gates + counts "Update Geometries", and gates Reset). */
  pendingGeometryCount: number;
  /** How many slots are scoped — only these can be moved or trimmed. */
  scopedSlotCount?: number;

  /** Live position + count of the chart selection, gating the nudge controls. */
  selectionInfo: GunBarrelSelectionInfo;
  /** Step the selected property by a delta (ft) on one axis. */
  onNudge: (axis: GunBarrelEditAxis, deltaFt: number) => void;
  /** Move the selected property to an exact value (ft) on one axis. */
  onSetAbsolute: (axis: GunBarrelEditAxis, valueFt: number) => void;
  /** Discard all pending position edits. */
  onResetEdits: () => void;

  /** Lateral length is editable — a single valued LTD is selected. */
  lengthEnabled?: boolean;
  /** The selected LTD's current lateral length (ft); null when not editable. */
  lengthFt: number | null;
  /** Its full (un-trimmed) length (ft) — the ceiling a length edit clamps to. */
  fullLengthFt: number | null;
  /** Which end a length edit is taken off. */
  trimFrom: TrimFrom;
  onTrimFromChange: (trimFrom: TrimFrom) => void;
  /** Step the lateral length by a delta (ft), trimming from the chosen end. */
  onNudgeLength: (deltaFt: number) => void;
  /** Set the lateral to an exact length (ft). */
  onSetLength: (valueFt: number) => void;

  /** The unit has no slots or wells to edit yet. */
  empty?: boolean;

  /** Merge Geometries; omitted when the unit cannot be merged into (a draft). */
  merge?: {
    onToggle: () => void;
    active: boolean;
    disabledReason: string | null;
    selectedCount: number;
    otherFormations: string[];
    otherFormationCount: number;
    /** The open panel, rendered under the actions. */
    panel: ReactNode;
    composites: {
      id: number;
      name: string;
      memberCount: number;
      producingLengthFt: number;
    }[];
    /** Omitted on a promoted workspace (composites are frozen). */
    onSplit?: (id: number) => void;
    /** The composite being split, until it leaves the list. */
    splittingId?: number | null;
  };
}

const roundFt = (v: number) => String(Math.round(v));

/**
 * The gun barrel pane's right-hand rail.
 *
 * The two commit paths are distinct, as the mock states: Generate LTD Inventory
 * re-lays the whole unit, Update Geometries re-cuts only the slots you moved —
 * which is why the latter stays disabled until something has been moved.
 *
 * Below them, Edit Property Geometries lets a single selected property be moved
 * by an exact value or nudged. Lateral length + Trim from trim a valued LTD's
 * lateral (front-end only), two-way synced with the map's endpoint grips.
 *
 * @param {UnitBuilderEditRailProps} props - Formation, labels, actions, nudge controls.
 * @return {JSX.Element} The rail.
 */
export const UnitBuilderEditRail: FC<UnitBuilderEditRailProps> = ({
  formationCode,
  onStepFormation,
  canStepUp = false,
  canStepDown = false,
  showSlotLabels,
  onShowSlotLabelsChange,
  onGenerateInventory,
  generating = false,
  onUpdateGeometries,
  updating = false,
  pendingGeometryCount,
  scopedSlotCount = 0,
  selectionInfo,
  onNudge,
  onSetAbsolute,
  onResetEdits,
  lengthEnabled = false,
  lengthFt,
  fullLengthFt,
  trimFrom,
  onTrimFromChange,
  onNudgeLength,
  onSetLength,
  empty = false,
  merge,
}) => {
  const single = selectionInfo.single;
  // X / TVD act on exactly one selected property. A permit the backend did not
  // mark landing-editable is frozen outright; an editable one moves in TVD only.
  const permitFrozen =
    (single?.isPermit ?? false) && !(single?.landingEditable ?? false);
  const xEnabled = single !== null && !single.isPermit;
  const tvdEnabled = single !== null && !permitFrozen;
  const placeholder = selectionInfo.count > 1 ? 'multiple' : '—';

  // Draft buffers so a user can type freely; null mirrors the live value. The
  // effects clear a draft once the committed value lands, so nudges show through.
  const [xDraft, setXDraft] = useState<string | null>(null);
  const [tvdDraft, setTvdDraft] = useState<string | null>(null);
  const [lengthDraft, setLengthDraft] = useState<string | null>(null);
  useEffect(() => setXDraft(null), [single?.xFt]);
  useEffect(() => setTvdDraft(null), [single?.tvdFt]);
  useEffect(() => setLengthDraft(null), [lengthFt]);

  const xValue = xDraft ?? (single ? roundFt(single.xFt) : '');
  const tvdValue = tvdDraft ?? (single ? roundFt(single.tvdFt) : '');
  const lengthValue =
    lengthDraft ??
    (lengthEnabled && lengthFt !== null ? roundFt(lengthFt) : '');
  // When several scoped laterals disagree, the field reads "multiple" (the mock);
  // typing a value converges them.
  const lengthPlaceholder =
    lengthEnabled && lengthFt === null ? 'multiple' : '—';

  const commit = (axis: GunBarrelEditAxis, raw: string | null) => {
    if (raw === null) return;
    const v = parseFloat(raw);
    if (Number.isFinite(v)) onSetAbsolute(axis, v);
  };
  const commitLength = (raw: string | null) => {
    if (raw === null) return;
    const v = parseFloat(raw);
    if (Number.isFinite(v)) onSetLength(v);
  };

  return (
    <aside className={style.editRail}>
      {/* Same row shape as the X / Y / Lateral nudge rows: label takes the slack,
          the value is fixed-width and right-aligned, arrows sit at the end. */}
      <div className={style.editMoveRow}>
        <span className={style.editMoveAxis}>Formation</span>
        <input
          type="text"
          readOnly
          className={style.formationInput}
          aria-label="Scoped formation"
          title="Scoped formation — use the arrows to flip shallower / deeper"
          value={formationCode ?? ''}
        />
        <Button
          kind="tertiary"
          size="sm"
          hasIconOnly
          renderIcon={ArrowUp}
          iconDescription="Shallower formation"
          tooltipPosition="top"
          disabled={!onStepFormation || !canStepUp}
          onClick={() => onStepFormation?.(-1)}
        />
        <Button
          kind="tertiary"
          size="sm"
          hasIconOnly
          renderIcon={ArrowDown}
          iconDescription="Deeper formation"
          tooltipPosition="top"
          disabled={!onStepFormation || !canStepDown}
          onClick={() => onStepFormation?.(1)}
        />
      </div>

      <div className={style.railCheckbox}>
        <Checkbox
          id="unit-builder-show-slot-labels"
          labelText="Show property labels on chart"
          checked={showSlotLabels}
          onChange={(_event, { checked }) => onShowSlotLabelsChange(checked)}
        />
      </div>

      <div className={style.editActions}>
        <Button
          kind="primary"
          size="sm"
          renderIcon={generating ? undefined : Renew}
          disabled={!onGenerateInventory || generating}
          onClick={onGenerateInventory}
        >
          {generating ? (
            <InlineLoading description="Generating..." />
          ) : (
            'Generate LTD Inventory'
          )}
        </Button>

        <Button
          kind="tertiary"
          size="sm"
          renderIcon={updating ? undefined : Renew}
          // No pending geometry edit — a move or a lateral trim — means nothing
          // to re-cut.
          disabled={
            !onUpdateGeometries || updating || pendingGeometryCount === 0
          }
          onClick={onUpdateGeometries}
        >
          {updating ? (
            <InlineLoading description="Updating..." />
          ) : pendingGeometryCount > 0 ? (
            `Update Geometries (${pendingGeometryCount})`
          ) : (
            'Update Geometries'
          )}
        </Button>

        {merge && (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Connect}
            aria-expanded={merge.active}
            disabled={!merge.active && merge.disabledReason !== null}
            title={
              merge.active
                ? 'Close Merge Geometries'
                : (merge.disabledReason ??
                  `Stitch the ${merge.selectedCount} selected LTDs into one composite wellbore`)
            }
            onClick={merge.onToggle}
          >
            Merge Geometries
          </Button>
        )}
        {merge && merge.otherFormationCount > 0 && (
          <span className={style.railHint}>
            Includes {merge.otherFormationCount} LTD
            {merge.otherFormationCount === 1 ? '' : 's'} from other formations (
            {merge.otherFormations.join(', ')}) — click a highlighted square to
            drop it.
          </span>
        )}
      </div>

      {merge?.active && merge.panel}

      {merge && merge.composites.length > 0 && (
        <div className={`${style.editSection} ${style.compositeSection}`}>
          <span className={style.editSectionLabel}>
            Composite wellbores ({merge.composites.length})
          </span>
          <ul className={style.compositeList}>
            {merge.composites.map((c) => (
              <li key={c.id} className={style.compositeRow}>
                <span className={style.compositeText}>
                  <span className={style.compositeName}>{c.name}</span>
                  <span className={style.railHint}>
                    {c.memberCount} LTDs ·{' '}
                    {formatNumber(c.producingLengthFt, 0, 0)} ft producing
                  </span>
                </span>
                {merge.onSplit && merge.splittingId === c.id && (
                  <InlineLoading
                    className={style.compositeSplitLoader}
                    status="active"
                    iconDescription="Splitting composite"
                  />
                )}
                {merge.onSplit && merge.splittingId !== c.id && (
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={Unlink}
                    iconDescription="Split composite"
                    tooltipPosition="top"
                    tooltipAlignment="end"
                    disabled={
                      merge.splittingId !== null &&
                      merge.splittingId !== undefined
                    }
                    onClick={() => merge.onSplit?.(c.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Edit Property Geometries — scoping is what gates moving a property, so
          the section heads with the scoped count. */}
      {!empty && (
        <div className={style.editSection}>
          <div className={style.editSectionHead}>
            <span className={style.editSectionLabel}>
              Edit Property Geometries ({scopedSlotCount} selected)
            </span>
            <Toggletip align="bottom-end" autoAlign>
              <ToggletipButton label="How to select and move properties">
                <Information />
              </ToggletipButton>
              <ToggletipContent>
                <p>
                  Select slots/wells in the Type Curve Wells table below, then
                  drag on the chart or nudge here. Slot depth is held inside the{' '}
                  {formationCode ?? 'selected'} interval — a slot stops at the
                  formation top or base.
                </p>
              </ToggletipContent>
            </Toggletip>
          </div>

          <p className={style.railHint}>
            Drag on the chart, type an exact value, or nudge with the arrows
            (100 ft across, 10 ft deep):
          </p>

          {/* X (section line) */}
          <div className={style.editMoveRow}>
            <span className={style.editMoveAxis}>X (section line)</span>
            <input
              type="text"
              inputMode="decimal"
              className={style.editMoveInput}
              aria-label="Section-line position (ft)"
              title={
                single?.isPermit
                  ? 'Permits move in TVD only — X is fixed'
                  : xEnabled
                    ? 'Section-line position (ft)'
                    : 'Select one property to type an exact position'
              }
              placeholder={placeholder}
              disabled={!xEnabled}
              value={xEnabled ? xValue : ''}
              onChange={(e) => setXDraft(e.target.value)}
              onBlur={() => commit('x', xDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit('x', xDraft);
              }}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={ArrowLeft}
              iconDescription={`−${X_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!xEnabled}
              onClick={() => onNudge('x', -X_STEP_FT)}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={ArrowRight}
              iconDescription={`+${X_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!xEnabled}
              onClick={() => onNudge('x', X_STEP_FT)}
            />
          </div>

          {/* Y (TVD) */}
          <div className={style.editMoveRow}>
            <span className={style.editMoveAxis}>Y (TVD)</span>
            <input
              type="text"
              inputMode="decimal"
              className={style.editMoveInput}
              aria-label="True vertical depth (ft)"
              title={
                permitFrozen
                  ? 'Permit positions are fixed'
                  : tvdEnabled
                    ? 'True vertical depth (ft)'
                    : 'Select one property to type an exact depth'
              }
              placeholder={placeholder}
              disabled={!tvdEnabled}
              value={tvdEnabled ? tvdValue : ''}
              onChange={(e) => setTvdDraft(e.target.value)}
              onBlur={() => commit('tvd', tvdDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit('tvd', tvdDraft);
              }}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={ArrowUp}
              iconDescription={`−${TVD_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!tvdEnabled}
              onClick={() => onNudge('tvd', -TVD_STEP_FT)}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={ArrowDown}
              iconDescription={`+${TVD_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!tvdEnabled}
              onClick={() => onNudge('tvd', TVD_STEP_FT)}
            />
          </div>

          {/* Lateral length + Trim from — front-end only: trimming valued LTD
              laterals is a local coords overlay shared with the map's endpoint
              grips. Applies to every scoped LTD at once (typing a value converges
              them; the arrows nudge each). Disabled unless a valued LTD is
              scoped; reads "multiple" when their lengths disagree. */}
          <div className={style.editMoveRow}>
            <span className={style.editMoveAxis}>Lateral length</span>
            <input
              type="text"
              inputMode="decimal"
              className={style.editMoveInput}
              aria-label="Perforated length (ft)"
              title={
                lengthEnabled
                  ? `Perforated length (ft) — trimmed from the ${
                      trimFrom === 'high' ? 'End' : 'Start'
                    }${
                      fullLengthFt !== null
                        ? `; full ${roundFt(fullLengthFt)} ft`
                        : ''
                    }`
                  : 'Select one valued LTD property to edit its lateral length'
              }
              placeholder={lengthPlaceholder}
              disabled={!lengthEnabled}
              value={lengthValue}
              onChange={(e) => setLengthDraft(e.target.value)}
              onBlur={() => commitLength(lengthDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitLength(lengthDraft);
              }}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={Subtract}
              iconDescription={`−${LENGTH_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!lengthEnabled}
              onClick={() => onNudgeLength(-LENGTH_STEP_FT)}
            />
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={Add}
              iconDescription={`+${LENGTH_STEP_FT} ft`}
              tooltipPosition="top"
              disabled={!lengthEnabled}
              onClick={() => onNudgeLength(LENGTH_STEP_FT)}
            />
          </div>

          <div className={style.editMoveRow}>
            <span className={style.editMoveAxis}>Trim from</span>
            <div
              className={style.trimSwitcher}
              title={
                lengthEnabled
                  ? 'Which end the length is taken off'
                  : 'Select one valued LTD property to trim'
              }
            >
              <ContentSwitcher
                size="sm"
                selectedIndex={trimFrom === 'low' ? 0 : 1}
                onChange={({ name }) =>
                  onTrimFromChange(name === 'low' ? 'low' : 'high')
                }
              >
                <Switch name="low" text="Start" disabled={!lengthEnabled} />
                <Switch name="high" text="End" disabled={!lengthEnabled} />
              </ContentSwitcher>
            </div>
          </div>

          <div className={style.editReset}>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Reset}
              disabled={pendingGeometryCount === 0}
              title="Clear pending property moves and lateral-length trims"
              onClick={onResetEdits}
            >
              Reset
            </Button>
          </div>
        </div>
      )}

      {empty && (
        <p className={style.railHint}>
          No properties to edit in this unit yet. Generate LTD geometries above
          to lay out laterals, or pick a different unit.
        </p>
      )}

      {/* Value/unvalue Notes — shown when a property is right-clicked. */}
      <UnitBuilderNotesSection />
    </aside>
  );
};
